// Fiók-kötés (ügyféladminisztrátor / kapcsolattartó) create/edit — jobb oldali beúszó FormSheet, KÉT módban
// (a contacts-minta: külön add/edit instance). ADD (`target == null`): identity-picker (LÉTEZŐ identity a
// GET /identities-ből, a kind-hoz illő típusra — admin→internal, contact→partner) + kind-váltó + kötés-flag-ek +
// managerType (csak adminnál). EDIT (`target != null`): az identity + kind FIX (a picker/kind zárolva), csak a
// flag-ek/managerType szerkeszthető; a jelenlegi értékek a detail `affiliationDetails`-éből. A validálás
// forrás-igazsága a @atlas/contracts (AccountAffiliationUpsertBodySchema) a features/accounts/affiliation-form.ts-en át.
import { type AccountDetailSchema } from '@atlas/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { type ComboboxOption } from '@/components/shared/entity-combobox';
import { ComboboxField, SelectField, SwitchField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  AFFILIATION_MANAGER_NONE,
  AFFILIATION_PORTAL_NONE,
  buildAffiliationResolver,
  EMPTY_AFFILIATION_FORM,
  toAffiliationBody,
  type AffiliationFormValues,
} from '@/features/accounts/affiliation-form';
import { accountKeys } from '@/features/accounts/api';
import { portalAccessKeys } from '@/features/accounts/portal-access-keys';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

type AccountDetail = z.infer<typeof AccountDetailSchema>;
type AffiliationDetail = AccountDetail['affiliationDetails'][number];

/** A szerkesztendő kötés cél-adata (edit): az identity + kind + a megjelenítendő név (a picker előtöltéséhez). */
export interface AffiliationEditTarget {
  identityId: string;
  kind: 'admin' | 'contact';
  name: string;
}

export interface AccountAffiliationFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  /** `null` → ÚJ kötés (add); egyébként a szerkesztendő kötés (identity + kind FIX). */
  target: AffiliationEditTarget | null;
  /** A fiók kötéseinek teljes flag-alakja (a detailből) — az edit-előtöltéshez. */
  affiliationDetails: AffiliationDetail[];
  /**
   * ADD-módban a kötés MINŐSÉGE előre eldöntve + zárolva. A fiók-detailen a két kötés-fajta KÜLÖN szekcióban él
   * (Kapcsolattartók · Ügyféladminisztrátorok), tehát a szekció „hozzáadás" gombja már megválaszolta a kérdést —
   * a választó szabadon hagyása csak azt engedné, hogy a felhasználó egy MÁS szekcióba vegyen fel sort, és a
   * mentés után ne találja. Edit-módban a `target.kind` amúgy is fix.
   */
  presetKind?: 'admin' | 'contact';
}

export function AccountAffiliationFormSheet({
  open,
  onOpenChange,
  accountId,
  target,
  affiliationDetails,
  presetKind,
}: AccountAffiliationFormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const isEdit = target !== null;

  // Edit-előtöltés: a (identity, kind) kötés jelenlegi flag-jei a detailből.
  const current = useMemo<AffiliationDetail | undefined>(
    () =>
      target
        ? affiliationDetails.find(
            (a) => a.identityId === target.identityId && a.kind === target.kind,
          )
        : undefined,
    [target, affiliationDetails],
  );

  const form = useForm<AffiliationFormValues>({
    resolver: buildAffiliationResolver(t),
    mode: 'onTouched',
    defaultValues: EMPTY_AFFILIATION_FORM,
  });
  const { reset, handleSubmit, setError, setValue, watch } = form;
  const kind = watch('kind');
  const isPrimary = watch('isPrimary');
  // A PORTÁL-SZEREP külön jog-kapun áll (a kötés-szerkesztés joga NEM jogosít hozzáférés-osztásra), és az
  // Odin-oldali képesség-scope-ot is nézzük — enélkül a mentés 403 lenne, tehát a mezőt meg sem jelenítjük.
  const queryClient = useQueryClient();
  const { can, hasScope } = usePermissions();
  const canManagePortal = can('portal_access.manage') && hasScope('iam:portal-access');
  // A portál-szerep CSAK kapcsolattartói kötésnél értelmes: az `admin` kötés belső kolléga (nem partner).
  const portalRoleVisible = canManagePortal && kind === 'contact';
  const portalQuery = api.portalAccessList.useQuery(portalAccessKeys.list(accountId), {
    params: { id: accountId },
  });
  const currentPortalRole = (() => {
    if (!target || portalQuery.data?.status !== 200) return AFFILIATION_PORTAL_NONE;
    const row = portalQuery.data.body.rows.find((r) => r.identityId === target.identityId);
    return row?.role ?? AFFILIATION_PORTAL_NONE;
  })();

  /** Az add-mód alapértékei: üres kötés a szekció által diktált minőséggel. */
  const emptyForm = useMemo<AffiliationFormValues>(
    () => (presetKind ? { ...EMPTY_AFFILIATION_FORM, kind: presetKind } : EMPTY_AFFILIATION_FORM),
    [presetKind],
  );

  // Előtöltés: edit → a cél + a detail-flag-ek; add → üres (a szekció minőségével).
  useEffect(() => {
    if (target) {
      reset({
        identityId: target.identityId,
        kind: target.kind,
        isPrimary: current?.isPrimary ?? false,
        portalAccess: current?.portalAccess ?? false,
        portalRole: currentPortalRole,
        managerType: current?.managerType ?? AFFILIATION_MANAGER_NONE,
      });
    } else {
      reset(emptyForm);
    }
  }, [target, current, currentPortalRole, emptyForm, reset]);

  // ADD-mód: a kind váltása más identity-pool → a kiválasztott identity-t nullázzuk (a picker újratölt).
  useEffect(() => {
    if (isEdit) return;
    setValue('identityId', '');
  }, [kind, isEdit, setValue]);

  // Az elsődleges kötés MINDIG kap portál-hozzáférést (szerver-invariáns), ezért a bitet bekapcsolva küldjük.
  // A SZEREPET viszont NEM állítjuk automatikusan: a „mit tehet" döntés az ügyintézőé (az elsődleges
  // kapcsolattartó jellemzően `portal-admin`, de ez nem kényszer).
  useEffect(() => {
    if (isPrimary) setValue('portalAccess', true);
  }, [isPrimary, setValue]);

  // Identity-kereső: a kind-hoz illő típusra szűrve (admin→belső kolléga, contact→partner). Best-effort: nincs
  // jog / hiba → üres opció-lista (a mező enélkül is renderel; a szerver-oldali izoláció változatlan).
  const searchIdentities = async (q: string): Promise<ComboboxOption[]> => {
    const type = kind === 'admin' ? 'internal' : 'partner';
    const r = await api.identities.query({ query: { q: q || undefined, type, limit: 20 } });
    if (r.status !== 200) return [];
    return r.body.identities.map((i) => ({
      id: i.id,
      label: i.displayName ?? i.email ?? i.id,
    }));
  };

  const initialOption: ComboboxOption | null = target
    ? { id: target.identityId, label: target.name }
    : null;

  const mutation = useEntityMutation<AffiliationFormValues>({
    mutationFn: (values) =>
      api.accountAffiliationUpsert.mutation({
        params: { id: accountId },
        body: toAffiliationBody(values),
      }),
    invalidateKeys: [accountKeys.detail(accountId), accountKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_AFFILIATION_FORM) {
        setError(field as keyof AffiliationFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit ? t('toast:accounts.affiliationSaved') : t('toast:accounts.affiliationAdded'),
      conflict: t('toast:accounts.conflict'),
      forbidden: t('toast:accounts.forbidden'),
      validation: t('toast:accounts.validation'),
      error: t('toast:accounts.error'),
    },
  });

  /**
   * PORTÁL-SZEREP mentése a kötés UTÁN. A sorrend KÖTÖTT: a BFF csak a fiók KÖTÖTT kapcsolattartójára enged
   * szerepet állítani, tehát az új kötésnek már léteznie kell. Ha a szerep-írás elbukik, a kötés akkor is
   * mentve van — a hibát toast jelzi, és a „Partner-portál hozzáférés" szekcióban egy kattintással pótolható.
   * A hiba IRÁNYA biztonságos: a hozzáférés elmarad, nem többletet ad.
   */
  const savePortalRole = async (values: AffiliationFormValues): Promise<void> => {
    if (!portalRoleVisible || values.portalRole === currentPortalRole) return;
    const res =
      values.portalRole === AFFILIATION_PORTAL_NONE
        ? await api.portalAccessRevoke.mutation({
            params: { id: accountId, identityId: values.identityId },
          })
        : await api.portalAccessSet.mutation({
            params: { id: accountId, identityId: values.identityId },
            body: { role: values.portalRole as 'portal-admin' | 'portal-member' | 'portal-viewer' },
          });
    if (res.status !== 200) throw new Error('portal-role');
  };

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        void savePortalRole(values)
          .catch(() => toast.error(t('toast:portalAccess.error')))
          .finally(() => {
            void queryClient.invalidateQueries({ queryKey: portalAccessKeys.list(accountId) });
            onOpenChange(false);
          });
      },
    });
  });

  const kindOptions = [
    { value: 'admin', label: t('accounts.affiliation.kindAdmin') },
    { value: 'contact', label: t('accounts.affiliation.kindContact') },
  ];
  // Növekvő privilégium — a fiók-oldali szekcióval és a jogosultság-oldal létrájával azonos sorrend.
  const portalRoleOptions = [
    { value: AFFILIATION_PORTAL_NONE, label: t('portalAccess.roles.none') },
    { value: 'portal-viewer', label: t('portalAccess.roles.portal-viewer') },
    { value: 'portal-member', label: t('portalAccess.roles.portal-member') },
    { value: 'portal-admin', label: t('portalAccess.roles.portal-admin') },
  ];
  const managerTypeOptions = [
    { value: AFFILIATION_MANAGER_NONE, label: t('accounts.affiliation.managerNone') },
    { value: 'digital_marketing', label: t('accounts.affiliation.managerDigitalMarketing') },
    { value: 'revenue_management', label: t('accounts.affiliation.managerRevenue') },
  ];

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(emptyForm);
        onOpenChange(o);
      }}
      title={isEdit ? t('accounts.affiliation.editTitle') : t('accounts.affiliation.addTitle')}
      description={isEdit ? target.name : t('accounts.affiliation.addDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('accounts.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('accounts.form.saving') : t('accounts.form.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        {/* Minőség (kind) — edit-módban FIX (a kötés gazdája nem címezhető át). */}
        {/* Minőség: edit-módban a kötés gazdája nem címezhető át; add-módban a SZEKCIÓ dönti el (presetKind). */}
        <SelectField<AffiliationFormValues>
          name="kind"
          label={t('accounts.affiliation.kind')}
          disabled={isEdit || presetKind !== undefined}
          options={kindOptions}
        />

        {/* Identity-picker — a kind-hoz illő típusra szűrve; edit-módban FIX (előtöltve + zárolva). */}
        <ComboboxField<AffiliationFormValues>
          name="identityId"
          label={t('accounts.affiliation.identity')}
          hint={
            kind === 'admin'
              ? t('accounts.affiliation.identityHintAdmin')
              : t('accounts.affiliation.identityHintContact')
          }
          required
          search={searchIdentities}
          initialOption={initialOption}
          placeholder={t('accounts.affiliation.identityPlaceholder')}
          clearable={false}
        />

        {/* Menedzser-típus — CSAK admin-kötésnél. */}
        {kind === 'admin' && (
          <SelectField<AffiliationFormValues>
            name="managerType"
            label={t('accounts.affiliation.managerType')}
            placeholder={t('accounts.affiliation.managerNone')}
            options={managerTypeOptions}
          />
        )}

        <SwitchField<AffiliationFormValues>
          name="isPrimary"
          label={t('accounts.affiliation.isPrimary')}
          hint={t('accounts.affiliation.isPrimaryHint')}
        />

        {/* PARTNER-PORTÁL SZEREP — a hozzáférés itt, a kötés felvételekor dől el (egy gondolat, egy művelet).
            A szerep FIÓKONKÉNTI: ugyanez a személy egy másik fiókon más szerepet viselhet. Jog nélkül (vagy
            admin-kötésnél) a mező nincs ott; a hozzáférések áttekintése a lap „Partner-portál hozzáférés"
            szekciója. */}
        {portalRoleVisible && (
          <SelectField<AffiliationFormValues>
            name="portalRole"
            label={t('portalAccess.roleLabel')}
            hint={t('portalAccess.roleHint')}
            options={portalRoleOptions}
          />
        )}
      </Form>
    </FormSheet>
  );
}
